// Pure ring geometry. World coords: ring centred at origin, +y down (SVG),
// angles in radians clockwise from 12 o'clock. See docs/SPEC.md.
// No DOM, no side effects.

export const RING_RADIUS = 1000;

export const TAU = Math.PI * 2;

export interface Point {
  x: number;
  y: number;
}

/** Viewport over the world: screen = world * scale + (tx, ty). */
export interface Camera {
  scale: number;
  tx: number;
  ty: number;
}

export interface Polar {
  angle: number;
  radius: number;
}

export interface MonthArc {
  /** Angle of the leading edge of the month's first day. */
  start: number;
  /** Angle of the trailing edge of the month's last day (> start; Dec ends at TAU). */
  end: number;
}

export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

/** Number of days in `month` (1..12) of `year`. */
export function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) throw new RangeError(`month out of range: ${month}`);
  if (month === 2 && isLeapYear(year)) return 29;
  return MONTH_LENGTHS[month - 1]!;
}

/** Days before the first of `month` within the year. */
function daysBeforeMonth(year: number, month: number): number {
  let total = 0;
  for (let m = 1; m < month; m++) total += daysInMonth(year, m);
  return total;
}

/**
 * 1-based ordinal of the date within its year (Jan 1 → 1).
 *
 * A day the month does not have is clamped to its last day rather than
 * refused: this runs for every entry on every render, and one impossible date
 * — a feed's typo, a row from before the schemas checked — must cost that one
 * mark its exact place, not the whole ring its picture.
 */
export function dayOfYear(year: number, month: number, day: number): number {
  if (month < 1 || month > 12) throw new RangeError(`month out of range: ${month}`);
  const clamped = Math.min(Math.max(day, 1), daysInMonth(year, month));
  return daysBeforeMonth(year, month) + clamped;
}

/**
 * Where a stored month/day actually lands in a given view year.
 * Feb 29 renders on Feb 28 in non-leap view years (SPEC). Identity otherwise.
 */
export function effectiveMonthDay(
  viewYear: number,
  month: number,
  day: number,
): { month: number; day: number } {
  if (month === 2 && day === 29 && !isLeapYear(viewYear)) return { month: 2, day: 28 };
  return { month, day };
}

/**
 * Angle of a date's *centre*: `TAU * (dayOfYear - 1 + 0.5) / daysInYear`,
 * clockwise from 12 o'clock. Feb 29 is folded onto Feb 28 in non-leap years,
 * and a day past the end of its month lands on the last day there is.
 */
export function dateToAngle(year: number, month: number, day: number): number {
  const eff = effectiveMonthDay(year, month, day);
  const doy = dayOfYear(year, eff.month, eff.day);
  return (TAU * (doy - 0.5)) / daysInYear(year);
}

/** Inverse of {@link dateToAngle}: the date whose cell contains `angle`. */
export function angleToDate(year: number, angle: number): CalendarDate {
  const n = daysInYear(year);
  const idx = Math.floor((normalizeAngle(angle) / TAU) * n);
  const doy = Math.min(n, Math.max(1, idx + 1));
  let remaining = doy;
  for (let month = 1; month <= 12; month++) {
    const len = daysInMonth(year, month);
    if (remaining <= len) return { year, month, day: remaining };
    remaining -= len;
  }
  /* istanbul ignore next -- unreachable: doy <= daysInYear */
  return { year, month: 12, day: 31 };
}

/** Angular span of a month: edge of its first day to edge of its last. */
export function monthArc(year: number, month: number): MonthArc {
  if (month < 1 || month > 12) throw new RangeError(`month out of range: ${month}`);
  const n = daysInYear(year);
  const before = daysBeforeMonth(year, month);
  return {
    start: (TAU * before) / n,
    end: (TAU * (before + daysInMonth(year, month))) / n,
  };
}

/** Polar → cartesian. angle 0 is 12 o'clock, +angle is clockwise, +y is down. */
export function polarToXY(angle: number, radius: number): Point {
  return { x: radius * Math.sin(angle), y: -radius * Math.cos(angle) };
}

/** Cartesian → polar; angle normalised to [0, TAU). */
export function xyToPolar(x: number, y: number): Polar {
  return { angle: normalizeAngle(Math.atan2(x, -y)), radius: Math.hypot(x, y) };
}

/** Wrap to [0, TAU). */
export function normalizeAngle(angle: number): number {
  const a = angle % TAU;
  return a < 0 ? a + TAU : a;
}

/** Shortest signed rotation from `from` to `to`, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  const d = normalizeAngle(to - from);
  return d > Math.PI ? d - TAU : d;
}

/**
 * Would text set along a clockwise arc at `angle` read upside down?
 *
 * Glyphs on a clockwise path stand up on the top half of the circle and hang
 * mirrored on the bottom half, so anything drawn along an arc has to reverse
 * its path down there. The decision belongs to the *on-screen* angle — the
 * ring rotates, and a July label brought to the top is no longer where it was
 * drawn — hence `rotation`, which is exactly what the ring layer is turned by.
 */
export function readsMirrored(angle: number, rotation = 0): boolean {
  const onScreen = normalizeAngle(angle + rotation);
  return onScreen > Math.PI / 2 && onScreen < (3 * Math.PI) / 2;
}

// --- camera -----------------------------------------------------------------

export function worldToScreen(camera: Camera, point: Point): Point {
  return { x: point.x * camera.scale + camera.tx, y: point.y * camera.scale + camera.ty };
}

export function screenToWorld(camera: Camera, point: Point): Point {
  return { x: (point.x - camera.tx) / camera.scale, y: (point.y - camera.ty) / camera.scale };
}

/** Scale by `factor`, keeping the world point under `screenPoint` fixed. */
export function zoomAbout(camera: Camera, screenPoint: Point, factor: number): Camera {
  const world = screenToWorld(camera, screenPoint);
  const scale = camera.scale * factor;
  return {
    scale,
    tx: screenPoint.x - world.x * scale,
    ty: screenPoint.y - world.y * scale,
  };
}

/** Camera that centres the ring and fits it inside the viewport with `padding` px of margin. */
export function fitCamera(viewportW: number, viewportH: number, padding = 0): Camera {
  const usable = Math.max(1, Math.min(viewportW, viewportH) - 2 * padding);
  return {
    scale: usable / (2 * RING_RADIUS),
    tx: viewportW / 2,
    ty: viewportH / 2,
  };
}

/** Rotate a world point about the ring centre (origin) by `rotation`, clockwise-positive. */
export function rotatePoint(point: Point, rotation: number): Point {
  const c = Math.cos(rotation);
  const s = Math.sin(rotation);
  // Clockwise in a +y-down frame is the standard CCW matrix.
  return { x: point.x * c - point.y * s, y: point.x * s + point.y * c };
}
